import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserDto } from '@user/services/user/models/user.dto';
import { Either, left, right } from 'src/app/helpers/either';
import { environment } from '../../../../environments/environment';
import { EitherApi } from '../either-api/either-api';
import { MeService } from '../me/me.service';
import { SessionStateService } from '../session/session-state.service';
import { AuthPayload } from './models/auth.payload';
import { FirstAccessPayload } from './models/first-access.payload';
import { ForgotPasswordPayload } from './models/forgot-password.payload';
import { ResetPasswordPayload } from './models/reset-password.payload';
import { TokenDto } from './models/token.dto';
import { RefreshTokenResult } from './refresh-token-result';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private readonly router: Router,
    private readonly meService: MeService,
    private readonly sessionState: SessionStateService
  ) {}

  private readonly eitherApi: EitherApi = new EitherApi();

  public async login(payload: AuthPayload): Promise<Either<HttpErrorResponse, UserDto>> {
    const response = await this.eitherApi.post<TokenDto>(environment.routes.auth.login, payload, true, { withCredentials: true });

    if (response.isLeft()) return left(response.value);

    const user = await this.meService.loadMe();

    if (user) {
      this.sessionState.clearRevoked();
      return right(user);
    }

    return left(
      new HttpErrorResponse({
        status: 0,
        error: {
          message: 'Erro ao carregar dados do usuário'
        }
      })
    );
  }

  public async completeFirstAccess(payload: FirstAccessPayload): Promise<Either<HttpErrorResponse, UserDto>> {
    const response = await this.eitherApi.post<TokenDto>(environment.routes.auth.firstAccessComplete, payload, true, { withCredentials: true });

    if (response.isLeft()) return left(response.value);

    const user = await this.meService.loadMe();

    if (user) {
      this.sessionState.clearRevoked();
      return right(user);
    }

    return left(
      new HttpErrorResponse({
        status: 0,
        error: {
          message: 'Erro ao carregar dados do usuário'
        }
      })
    );
  }

  public async requestPasswordReset(payload: ForgotPasswordPayload): Promise<Either<HttpErrorResponse, void>> {
    return this.eitherApi.post<void>(environment.routes.auth.forgotPassword, payload, true);
  }

  public async resetPassword(payload: ResetPasswordPayload): Promise<Either<HttpErrorResponse, void>> {
    return this.eitherApi.post<void>(environment.routes.auth.resetPassword, payload, true);
  }

  public async refreshToken(): Promise<RefreshTokenResult> {
    const response = await this.eitherApi.post<TokenDto>(environment.routes.auth.refresh, {}, true, { withCredentials: true });

    if (response.isRight()) {
      return 'success';
    }

    if (response.value.status === 0) {
      return 'networkError';
    }

    return 'authFailed';
  }

  public async logout(): Promise<void> {
    this.sessionState.markRevoked();

    await this.eitherApi.post<void>(environment.routes.auth.logout, {}, true, { withCredentials: true });

    this.meService.me = null;

    await this.router.navigateByUrl(environment.configs.unauthenticatedRoute, { replaceUrl: true });
  }
}
